import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { createAbonnement } from "@/features/abonnementen/abonnementen.api";
import { listKlanten } from "@/features/klanten/klanten.api";
import { listPakketten } from "@/features/pakketten/pakketten.api";
import { ApiError } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

export const Route = createFileRoute("/_shell/abonnementen/new")({
  component: NewAbonnementPage,
});

function NewAbonnementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const klantenQuery = useQuery({
    queryKey: ["klanten", { pageSize: "200" }],
    queryFn: () => listKlanten({ pageSize: "200" }),
  });
  // De hele catalogus in één keer: 150–200 codes is klein genoeg om lokaal te
  // filteren, en dat scheelt een verzoek per toetsaanslag.
  const pakkettenQuery = useQuery({
    queryKey: ["pakketten", { pageSize: "250" }],
    queryFn: () => listPakketten({ pageSize: "250" }),
  });
  // Alleen actieve codes zijn verkoopbaar; concept en uitgefaseerd niet.
  const pakketten = (pakkettenQuery.data?.data ?? []).filter((p) => p.kiesbaar);

  const [klantId, setKlantId] = useState("");
  const [gekozen, setGekozen] = useState<string[]>([]);
  const [zoek, setZoek] = useState("");
  const [alsProef, setAlsProef] = useState(false);
  const [looptijd, setLooptijd] = useState(12);
  const [startdatum, setStartdatum] = useState("");
  // Facturatie is per jaar; maand bestaat alleen voor een betaalde proef.
  const [facturatie, setFacturatie] = useState<"maand" | "jaar">("jaar");
  const [betaalwijze, setBetaalwijze] = useState<"incasso" | "factuur">("incasso");
  const [maakContract, setMaakContract] = useState(true);

  const selectie = pakketten.filter((p) => gekozen.includes(p.id));
  // Een proef is € 0; anders is de standaardprijs het uitgangspunt.
  const maandbedragCents = alsProef
    ? 0
    : selectie.reduce((som, p) => som + p.maandtariefCents, 0);

  const zichtbaar = zoek.trim()
    ? pakketten.filter((p) =>
        `${p.productcode} ${p.naam} ${p.model}`.toLowerCase().includes(zoek.trim().toLowerCase()),
      )
    : pakketten;

  const toggle = (id: string) =>
    setGekozen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const create = useMutation({
    mutationFn: () =>
      createAbonnement({
        klantId,
        onderdelen: selectie.map((p) => ({
          pakketId: p.id,
          status: alsProef ? ("proef" as const) : ("besteld" as const),
          ...(alsProef ? { overeengekomenMaandbedragCents: 0 } : {}),
        })),
        startdatum: startdatum ? new Date(startdatum).toISOString() : undefined,
        looptijdMaanden: looptijd,
        opzegtermijnMaanden: 1,
        automatischeVerlenging: true,
        facturatie,
        betaalwijze,
        maakContract,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["abonnementen"] });
      void navigate({ to: "/abonnementen/$id", params: { id: created.nummer } });
    },
  });

  const canSubmit = Boolean(klantId) && selectie.length > 0 && !create.isPending;

  return (
    <div className="app-page" data-screen-label="Backend Abonnement — nieuw">
      <div className="page-header">
        <div className="page-header__title">
          <h1>{t("abonnementNieuw.title")}</h1>
        </div>
        <div className="page-header__actions">
          <Link to="/abonnementen" className="dtw-btn dtw-btn--ghost">
            {t("action.cancel")}
          </Link>
        </div>
      </div>

      <div className="create-grid">
        <div className="create-form">
          {/* Klant */}
          <div className="dtw-card">
            <div className="dtw-card__header">
              <span className="dtw-card__title">{t("abonnementNieuw.step.klant")}</span>
            </div>
            <div className="dtw-card__body">
              <div className="dtw-field">
                <label className="dtw-field__label">{t("abonnement.field.klant")}</label>
                <select className="dtw-select" value={klantId} onChange={(e) => setKlantId(e.target.value)}>
                  <option value="">—</option>
                  {(klantenQuery.data?.data ?? []).map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.naam}
                      {k.plaats ? ` · ${k.plaats}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pakket */}
          <div className="dtw-card">
            <div className="dtw-card__header">
              <span className="dtw-card__title">{t("abonnementNieuw.step.pakket")}</span>
            </div>
            <div className="dtw-card__body">
              <div
                className="dtw-input-group"
                style={{ marginBottom: "var(--space-3)", height: "var(--control-height-sm)" }}
              >
                <span className="dtw-input-group__icon">
                  <Icon name="search" size={13} />
                </span>
                <input
                  placeholder={t("pakketten.search")}
                  value={zoek}
                  onChange={(e) => setZoek(e.target.value)}
                />
              </div>
              <div className="pick-list">
                {zichtbaar.map((p) => (
                  <label className="pick-row" key={p.id}>
                    <input
                      type="checkbox"
                      checked={gekozen.includes(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                    <span className="pick-row__code mono">{p.productcode}</span>
                    <span className="pick-row__name">
                      {p.productgroepLabel} {p.model}
                      <span className="t-tertiary"> · {p.niveauLabel}</span>
                    </span>
                    <span className="pick-row__price mono num">{fmtMoney(p.maandtariefCents)}</span>
                  </label>
                ))}
                {zichtbaar.length === 0 && (
                  <p className="t-tertiary" style={{ fontSize: "var(--fs-sm)" }}>
                    {t("pakketten.empty.title")}
                  </p>
                )}
              </div>
              {/* Een proef is nog geen bestelling: € 0 en status Proef per onderdeel. */}
              <label className="login-dev__role" style={{ marginTop: "var(--space-3)" }}>
                <input type="checkbox" checked={alsProef} onChange={(e) => setAlsProef(e.target.checked)} />
                {t("abonnementNieuw.alsProef")}
              </label>
            </div>
          </div>

          {/* Looptijd & start */}
          <div className="dtw-card">
            <div className="dtw-card__header">
              <span className="dtw-card__title">{t("abonnementNieuw.step.looptijd")}</span>
            </div>
            <div className="dtw-card__body">
              <div className="fields">
                <div className="dtw-field">
                  <label className="dtw-field__label">{t("abonnement.field.looptijd")}</label>
                  <select
                    className="dtw-select"
                    value={looptijd}
                    onChange={(e) => setLooptijd(Number(e.target.value))}
                  >
                    {[12, 24, 36].map((m) => (
                      <option key={m} value={m}>
                        {t("abonnement.field.maanden", { count: m })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="dtw-field">
                  <label className="dtw-field__label">{t("abonnement.field.startdatum")}</label>
                  <input
                    className="dtw-input"
                    type="date"
                    value={startdatum}
                    onChange={(e) => setStartdatum(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Facturatie */}
          <div className="dtw-card">
            <div className="dtw-card__header">
              <span className="dtw-card__title">{t("abonnementNieuw.step.facturatie")}</span>
            </div>
            <div className="dtw-card__body">
              <div className="fields">
                <div className="dtw-field">
                  <label className="dtw-field__label">{t("abonnement.field.facturatie")}</label>
                  <select
                    className="dtw-select"
                    value={facturatie}
                    onChange={(e) => setFacturatie(e.target.value as "maand" | "jaar")}
                  >
                    <option value="maand">Per maand</option>
                    <option value="jaar">Per jaar</option>
                  </select>
                </div>
                <div className="dtw-field">
                  <label className="dtw-field__label">{t("abonnement.field.betaalwijze")}</label>
                  <select
                    className="dtw-select"
                    value={betaalwijze}
                    onChange={(e) => setBetaalwijze(e.target.value as "incasso" | "factuur")}
                  >
                    <option value="incasso">Automatische incasso</option>
                    <option value="factuur">Op factuur</option>
                  </select>
                </div>
              </div>
              <label className="login-dev__role" style={{ marginTop: "var(--space-3)" }}>
                <input type="checkbox" checked={maakContract} onChange={(e) => setMaakContract(e.target.checked)} />
                Direct een contract aanmaken
              </label>
            </div>
          </div>
        </div>

        {/* Sticky samenvatting */}
        <div className="summary-sticky">
          <div className="dtw-card">
            <div className="dtw-card__header">
              <span className="dtw-card__title">{t("abonnementNieuw.summary")}</span>
            </div>
            <div className="dtw-card__body">
              <dl className="kv">
                <dt>{t("abonnement.field.pakket")}</dt>
                <dd>
                  {selectie.length === 0 ? (
                    <span className="t-tertiary">{t("abonnementNieuw.geenOnderdelen")}</span>
                  ) : (
                    <span className="dtw-vstack" style={{ gap: "var(--space-1)" }}>
                      {selectie.map((p) => (
                        <span key={p.id}>
                          <span className="mono">{p.productcode}</span>{" "}
                          <span className="t-tertiary">{fmtMoney(p.maandtariefCents)}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </dd>
                <dt>{t("abonnement.field.maandbedrag")}</dt>
                <dd className="mono">{fmtMoney(maandbedragCents)}</dd>
                <dt>{t("abonnement.field.jaarbedrag")}</dt>
                <dd className="mono">{fmtMoney(maandbedragCents * 12)}</dd>
                <dt>{t("abonnement.field.looptijd")}</dt>
                <dd>{t("abonnement.field.maanden", { count: looptijd })}</dd>
                <dt>{t("abonnement.field.facturatie")}</dt>
                <dd>{facturatie === "maand" ? "Per maand" : "Per jaar"}</dd>
              </dl>
            </div>
          </div>

          <div className="dtw-alert dtw-alert--info">
            <Icon name="info" size={16} />
            <div>{t("abonnementNieuw.summaryNote")}</div>
          </div>

          {create.isError && (
            <div className="dtw-alert dtw-alert--danger">
              <Icon name="alert-circle" size={16} />
              <div>{create.error instanceof ApiError ? create.error.message : t("common.error")}</div>
            </div>
          )}

          <button
            className="dtw-btn dtw-btn--accent dtw-btn--lg"
            style={{ width: "100%" }}
            disabled={!canSubmit}
            onClick={() => create.mutate()}
          >
            <Icon name="plus-circle" size={16} /> {t("abonnementNieuw.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
